import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Tools|Credly|Templates|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ShareCredlyBadging,
	portal: 'SP',
};

type LocNameStart = 'SPToolCredlyTemplates';
export type SPToolCredlyTemplatesLocNames =
	| 'SPToolCredlyTemplatesNotificationExpiredCerts'
	| 'SPToolCredlyTemplatesTitleTab'
	| 'SPToolCredlyTemplatesTitlePageSection'
	| 'SPToolCredlyTemplatesNotificationEmailOn'
	| 'SPToolCredlyTemplatesContentNotFound'
	| 'SPToolCredlyTemplatesLabelName'
	| 'SPToolCredlyTemplatesLabelNameAbbr'
	| 'SPToolCredlyTemplatesLabelCatalogId'
	| 'SPToolCredlyTemplatesLabelType'
	| 'SPToolCredlyTemplatesLabelTemplateName'
	| 'SPToolCredlyTemplatesLabelTemplateId'
	| 'SPToolCredlyTemplatesLabelStatus'
	| 'SPToolCredlyTemplatesLabelExpireDate'
	| 'SPToolCredlyTemplatesButtonEditExpireDate'
	| 'SPToolCredlyTemplatesButtonEditExpireDateTooltip'
	| 'SPToolCredlyTemplatesErrorRefreshFailed';

export const spToolCredlyTemplatesLocs: LocDefs<SPToolCredlyTemplatesLocNames> = {
	SPToolCredlyTemplatesNotificationExpiredCerts: {
		defaultValue: 'Expired certs are being issued to Credly',
		description: 'Notification message for expired certs being issued to Credly',
		displayPath: `${pathStart}Expired certs`,
		displayType: 'Notification',
		legacyName: 'Share.CredlyBadging/Credly.BadgeSearch.Notification.ExpiredCerts',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPToolCredlyTemplatesTitleTab: {
		defaultValue: 'Credly Templates',
		description: 'Tab title',
		displayPath: `${pathStart}Tab title`,
		displayType: 'Title',
		legacyName: 'Share.CredlyBadging/Credly.Templates.Title.Tab',
		...sharedProperties,
	},
	SPToolCredlyTemplatesTitlePageSection: {
		defaultValue: 'Credly Templates',
		description: 'Page Section title',
		displayPath: `${pathStart}Page Section title`,
		displayType: 'Title',
		legacyName: 'Share.CredlyBadging/Credly.Templates.PageSectionTitle.CredlyTemplates',
		...sharedProperties,
	},
	SPToolCredlyTemplatesNotificationEmailOn: {
		defaultValue: 'Email notifications are turned on at Credly',
		description: 'Notification message for email notifications being turned on at Credly',
		displayPath: `${pathStart}Emails On`,
		displayType: 'Notification',
		legacyName: 'Share.CredlyBadging/Credly.Templates.Notification.EmailsIssued',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPToolCredlyTemplatesContentNotFound: {
		defaultValue: 'Not Found',
		description: 'Name not found message',
		displayPath: `${pathStart}Not Found`,
		displayType: 'Content',
		legacyName: 'Share.CredlyBadging/Credly.Templates.Content.CatalogNameNotFound',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelName: {
		defaultValue: 'Name',
		description: 'Column heading for: Name.',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.Name',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelNameAbbr: {
		defaultValue: 'Abbreviated',
		description: 'Column heading for: Abbreviated.',
		displayPath: `${pathStart}Abbreviated`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.Abbreviated',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelCatalogId: {
		defaultValue: 'Certmetrics ID',
		description: 'Column heading for: Certmetrics ID.',
		displayPath: `${pathStart}Certmetrics ID`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.CatalogId',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelType: {
		defaultValue: 'Type',
		description: 'Column heading for: Type.',
		displayPath: `${pathStart}Type`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.Type',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelTemplateName: {
		defaultValue: 'Template Name',
		description: 'Column heading for: Template Name.',
		displayPath: `${pathStart}Template Name`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.TemplateName',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelTemplateId: {
		defaultValue: 'Template ID',
		description: 'Column heading for: Template ID.',
		displayPath: `${pathStart}Template ID`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.TemplateId',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelStatus: {
		defaultValue: 'Status',
		description: 'Column heading for: Status.',
		displayPath: `${pathStart}Status`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.TemplateStatus',
		...sharedProperties,
	},
	SPToolCredlyTemplatesLabelExpireDate: {
		defaultValue: 'Expire Date',
		description: 'Column heading for: Expire Date.',
		displayPath: `${pathStart}Expire Date`,
		displayType: 'Label',
		legacyName: 'Share.CredlyBadging/Credly.Templates.ColumnHeading.TemplateExpireDate',
		...sharedProperties,
	},
	SPToolCredlyTemplatesButtonEditExpireDate: {
		defaultValue: 'Edit',
		description: 'Button text for editing expire date.',
		displayPath: `${pathStart}Button Edit Expire Date`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPToolCredlyTemplatesButtonEditExpireDateTooltip: {
		defaultValue: 'Edit expire date',
		description: 'Tooltip for the edit expire date button.',
		displayPath: `${pathStart}Button Edit Expire Date Tooltip`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPToolCredlyTemplatesErrorRefreshFailed: {
		defaultValue: 'Failed to refresh Credly catalog data.',
		description: 'Error message when refreshing Credly catalog data fails.',
		displayPath: `${pathStart}Error Refresh Failed`,
		displayType: 'Error',
		...sharedProperties,
	},
};

export type SPToolCredlyTemplatesLocType = LocMap<SPToolCredlyTemplatesLocNames>;
